/* eslint-disable react/react-in-jsx-scope */
import { ALL_OPTIONS_VALUES, ConfirmationModal, OrganizationAccountTypeSelector, OrganizationColumnsSelector, OrganizationPlanAndBillingSelector, OrganizationStatusSelector, OrganizationTeamMetricsSelector, PlanAndBillingValue } from '@components';
import {
  BillingInterval,
  BillingModel,
  ClientOrganization,
  OrganizationBilling,
  OrganizationStatus,
  OrganizationTierCategory,
  SubscriptionStatus,
  TierCodes,
} from '@graphql';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import Circle from '@mui/icons-material/Circle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import { Button, Chip, Divider, TextField, Tooltip, Typography } from '@mui/material';
import { Box, SxProps } from '@mui/system';
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridDensity,
  GridToolbarContainer
} from '@mui/x-data-grid';
import { COLORS } from '@styles';
import { appContext, paths, FILTERS_LABEL_MAP } from '@utils';
import { observer } from 'mobx-react';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Close } from '@mui/icons-material';
import { AssignManager } from '../AssignManager';
import { ManageAllDiscountsDrawer } from '../ManageAllDiscountsDrawer';
import { OrganizationDrawer } from '../OrganizationDrawer';
import { OrganizationFeaturesDrawer } from '../OrganizationFeaturesDrawer';
import { formatDate, getBillingCategory } from '../../utils';
import { ManagePlansAndSeats } from '../ManagePlansAndSeats/ManagePlansAndSeats';

interface PlanTooltipProps {
  organization: ClientOrganization;
}

interface PlanAndBillingProps {
  planType: PlanType[];
  billingStatus: BillingStatus[];
  billingMethod: BillingMethod[];
  billingInterval: BillingIntervalSelector[];
}

export enum BillingIntervalSelector {
  ALL = "AllIntervals",
  MONTHLY = BillingInterval.monthly,
  YEARLY = BillingInterval.yearly,
}

export enum BillingStatus {
  ALL = "AllStatuses",
  ACTIVE = SubscriptionStatus.Active,
  CANCELLED = SubscriptionStatus.Canceled,
  TRIALING = SubscriptionStatus.Trialing,
}

export enum PlanType {
  ALL = "AllTypes",
  ENTERPRISE = OrganizationTierCategory.Enterprise,
  PRO = OrganizationTierCategory.Pro,
  CORE = OrganizationTierCategory.Core,
}

export enum BillingMethod {
  CREDIT_CARD = BillingModel.CREDIT_CARD,
  INVOICE = BillingModel.INVOICE,
  ALL = "AllMethods",
}

interface TeamMetricsProps {
  minEmployees: number | undefined;
  maxEmployees: number | undefined;
}

export type TableColumns = "id" | "name" | "organizationPlan" | "renewalDate" | "employees" | "seatsUsage" | "admin" | "advisor"

interface TableSettingsProps {
  columns: TableColumns[];
  tableDensity: GridDensity;
}

// Mirrors the "Demo Account" switch in OrganizationDrawer: an org is a demo account
// when it is not flagged as a paying customer.
export enum AccountType {
  ALL = "All",
  DEMO = "Demo",
  PAYING = "Paying",
}

export interface FilterProps {
  orgStatus: OrganizationStatus | "All";
  accountType: AccountType;
  planAndBilling: PlanAndBillingProps;
  teamMetrics: TeamMetricsProps;
  tableSettings: TableSettingsProps;
}

function PlanTooltip({ organization }: PlanTooltipProps) {
  const orgAddons = organization.organizationFeatures.filter(
    (feature) => feature.type === 'add-on',
  );

  if (organization?.currentTier?.name === 'Core' || !orgAddons.length) {
    return (
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }} color={COLORS.NEUTRAL_DARK_2}>
        No Extensions are available for this plan.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }} color={COLORS.NEUTRAL_DARK_2}>
        Add-Ons:
      </Typography>
      <br />
      {orgAddons.map(({ enabledAt, feature }) => (
        <Typography
          key={feature.id}
          sx={{ fontSize: '0.875rem', fontWeight: 400 }}
          color={COLORS.NEUTRAL_DARK_2}
        >
          {enabledAt ? '✅' : '❌'} {feature.name}
        </Typography>
      ))}
    </Box>
  );
}

interface FilterChipProps {
  label: string;
  onRemoveBilling?: (filterValue: any, filterKey: keyof FilterProps["planAndBilling"]) => void;
  onRemoveTeamMetrics?: () => void;
  filterKey?: keyof FilterProps["planAndBilling"];
  filterValue?: any;
}

function FilterChip({ label, onRemoveBilling, onRemoveTeamMetrics, filterKey, filterValue }: FilterChipProps) {
  const CHIP_STYLE: SxProps = { backgroundColor: COLORS.HELP_NEUTRAL, color: COLORS.NEUTRAL_WHITE, borderRadius: '0.5rem' };

  const onRemove = () => {
    if (filterKey && filterValue && onRemoveBilling) {
      onRemoveBilling(filterValue, filterKey);
    } else if (onRemoveTeamMetrics) {
      onRemoveTeamMetrics?.();
    }
  };

  return <Chip label={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>{label} <Close sx={{ cursor: 'pointer' }} onClick={onRemove} /> </Typography>} sx={CHIP_STYLE} />;
}

interface CustomToolbarProps {
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  filter: FilterProps;
  setFilter: Dispatch<SetStateAction<FilterProps>>;
}

const CustomToolbar = observer(({ searchQuery, setSearchQuery, filter, setFilter }: CustomToolbarProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [openManageAllDiscountsDrawer, setOpenManageAllDiscountsDrawer] = useState<boolean>(false);
  const {
    advisorStore: { advisors },
  } = useContext(appContext);

  const planAndBillingFilters = filter?.planAndBilling
  const teamMetricsFilters = filter?.teamMetrics

  const teamMetricsFilter = useMemo(() => teamMetricsFilters?.minEmployees !== undefined || teamMetricsFilters?.maxEmployees !== undefined, [teamMetricsFilters]);
  const planAndBillingFilter = useMemo(() => planAndBillingFilters?.planType?.length > 0 || planAndBillingFilters?.billingStatus?.length > 0 || planAndBillingFilters?.billingMethod?.length > 0 || planAndBillingFilters?.billingInterval?.length > 0, [planAndBillingFilters]);

  const hasFilters = useMemo(() => teamMetricsFilter || planAndBillingFilter, [teamMetricsFilter, planAndBillingFilter]);

  const handleRemovePlanAndBillingFilter = (filterValue: any, filterKey: keyof FilterProps["planAndBilling"]) => {
    setFilter(prev => {
      const currentArray = prev.planAndBilling[filterKey];
      const filteredArray = Array.isArray(currentArray)
        ? currentArray.filter((item: any) => item !== filterValue && !ALL_OPTIONS_VALUES.includes(item as PlanAndBillingValue))
        : [];
      return { ...prev, planAndBilling: { ...prev.planAndBilling, [filterKey]: filteredArray } };
    });
  };

  const handleRemoveTeamMetricsFilter = (filedToRemove: 'minEmployees' | 'maxEmployees') => () => {
    setFilter(prev => ({ ...prev, teamMetrics: { ...prev.teamMetrics, [filedToRemove]: undefined } }));
  };

  const handleClearAllFilters = () => {
    setFilter(prev => ({ ...prev, planAndBilling: {} as FilterProps["planAndBilling"], teamMetrics: {} as FilterProps["teamMetrics"] }));
  };

  const handleClose = () => setOpen(!open);
  const handleManageAllDiscountsDrawerClose = () => setOpenManageAllDiscountsDrawer(!openManageAllDiscountsDrawer);

  return (
    <>
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <Box display='flex' justifyContent='space-between' width='100%'>
          <Box display='flex' alignItems='center' gap={1}>
            <OrganizationStatusSelector value={filter.orgStatus} setValue={setFilter} />
            <Divider orientation='vertical' flexItem />
            <OrganizationAccountTypeSelector value={filter.accountType} setValue={setFilter} />
            <Divider orientation='vertical' flexItem />
            <OrganizationPlanAndBillingSelector setValue={setFilter} value={filter.planAndBilling} />
            <Divider orientation='vertical' flexItem />
            <OrganizationTeamMetricsSelector setValue={setFilter} value={filter.teamMetrics} />
            <Divider orientation='vertical' flexItem />
            <OrganizationColumnsSelector setValue={setFilter} value={filter.tableSettings} />
            <Divider orientation='vertical' flexItem />
            <Button style={{ gap: '0.25rem' }} onClick={handleManageAllDiscountsDrawerClose}>
              <PercentOutlinedIcon />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Discount Codes</Typography>
            </Button>
          </Box>
          <div style={{ display: `flex`, alignItems: `center` }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{ marginRight: 16, width: `23rem` }}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
            <Button variant="contained" onClick={handleClose}>
              Create Organization
            </Button>
          </div>
        </Box>


        {hasFilters && <Box sx={{ marginTop: '0.5rem' }} display='flex' gap='0.5rem' flexWrap='wrap'>
          {planAndBillingFilters?.billingInterval?.filter(interval => interval !== BillingIntervalSelector.ALL)?.map((interval) => (
            <FilterChip key={interval} label={FILTERS_LABEL_MAP[interval]} onRemoveBilling={handleRemovePlanAndBillingFilter} filterKey="billingInterval" filterValue={interval} />
          ))}

          {planAndBillingFilters?.billingMethod?.filter(method => method !== BillingMethod.ALL)?.map((method) => (
            <FilterChip key={method} label={FILTERS_LABEL_MAP[method]} onRemoveBilling={handleRemovePlanAndBillingFilter} filterKey="billingMethod" filterValue={method} />
          ))}

          {planAndBillingFilters?.planType?.filter(type => type !== PlanType.ALL)?.map((type) => (
            <FilterChip key={type} label={FILTERS_LABEL_MAP[type]} onRemoveBilling={handleRemovePlanAndBillingFilter} filterKey="planType" filterValue={type} />
          ))}

          {planAndBillingFilters?.billingStatus?.filter(status => status !== BillingStatus.ALL)?.map((status) => (
            <FilterChip key={status} label={FILTERS_LABEL_MAP[status]} onRemoveBilling={handleRemovePlanAndBillingFilter} filterKey="billingStatus" filterValue={status} />
          ))}

          {teamMetricsFilters?.minEmployees !== undefined && (
            <FilterChip label={`At least ${teamMetricsFilters?.minEmployees} employees`} onRemoveTeamMetrics={handleRemoveTeamMetricsFilter('minEmployees')} />
          )}

          {teamMetricsFilters?.maxEmployees !== undefined && (
            <FilterChip label={`Up to ${teamMetricsFilters?.maxEmployees} employees`} onRemoveTeamMetrics={handleRemoveTeamMetricsFilter('maxEmployees')} />
          )}

          <Button variant='outlined' sx={{ height: '100%', padding: "0.25rem 1rem", fontSize: '0.75rem', fontWeight: 600, color: COLORS.NEUTRAL_WHITE, borderRadius: "0.25rem", borderColor: COLORS.NEUTRAL_LIGHT_1 }} onClick={handleClearAllFilters}>
            Clear All
          </Button>
        </Box>}
      </GridToolbarContainer >

      <OrganizationDrawer open={open} onClose={handleClose} advisors={advisors} />
      <ManageAllDiscountsDrawer open={openManageAllDiscountsDrawer} onClose={handleManageAllDiscountsDrawerClose} />
    </>
  );
});

const getOrganizationBillingData = (organizationBilling: OrganizationBilling[] | undefined) => {

  if (!organizationBilling?.length || organizationBilling.length === 0) {
    return null;
  }

  const firstBilling = organizationBilling[0];

  if (!firstBilling) {
    return null;
  }

  return firstBilling;
}

export const AllOrganizationsTable = observer(() => {
  const [openManagerModal, setOpenManagerModal] = useState<boolean>(false);
  const [org, setOrg] = useState<ClientOrganization | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openManagePlansAndSeatsModal, setOpenManagePlansAndSeatsModal] = useState<boolean>(false);
  const [openFeaturesDrawer, setOpenFeaturesDrawer] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterProps>({
    planAndBilling: {
      planType: [],
      billingStatus: [],
      billingMethod: [],
      billingInterval: [],
    },
    orgStatus: "All",
    accountType: AccountType.ALL,
    teamMetrics: { maxEmployees: undefined, minEmployees: undefined },
    tableSettings: {
      columns: ["id", "name", "organizationPlan", "renewalDate", "employees", "seatsUsage", "admin", "advisor"],
      tableDensity: 'standard',
    },
  } as FilterProps);
  const {
    organizationStore: {
      organizations: allOrganizations,
      organizationsResponse,
      fetchOrganizations,
      resetOrganizations,
      deleteOrganization,
      getOrgIdsByFeatureFlagConfig,
    },
    advisorStore: { advisors, fetchAdvisors },
    managersStore: { fetchAllManagers, resetManagers },
    historyStore: { history },
    tiersStore: { getTiers },
  } = useContext(appContext);

  useEffect(() => {
    fetchOrganizations();
    fetchAdvisors();
    getTiers();
  }, [fetchOrganizations, fetchAdvisors, getTiers]);

  useEffect(() => resetOrganizations, [resetOrganizations]);

  useEffect(() => {
    fetchAllManagers();
    return () => resetManagers();
  }, [fetchAllManagers, resetManagers]);

  useEffect(() => {
    getOrgIdsByFeatureFlagConfig();
  }, [getOrgIdsByFeatureFlagConfig]);

  const handleManagePlansAndSeatsModal = useCallback(() => {
    setOpenManagePlansAndSeatsModal(!openManagePlansAndSeatsModal);
  }, [openManagePlansAndSeatsModal]);

  const handleManagerModal = useCallback(() => {
    setOpenManagerModal(!openManagerModal);
  }, [openManagerModal]);

  const handleDeleteModal = useCallback(() => {
    setOpenDeleteModal(!openDeleteModal);
  }, [openDeleteModal]);

  const handleEditModal = useCallback(() => {
    setOpenEditModal(!openEditModal);
  }, [openEditModal]);

  const handleFeaturesDrawer = useCallback(() => {
    setOpenFeaturesDrawer(!openFeaturesDrawer);
  }, [openFeaturesDrawer]);

  const handleDeleteOrganization = useCallback(async () => {
    if (!org) return;

    const result = await deleteOrganization({ id: org?.id });
    if (result) {
      await fetchOrganizations();
      handleDeleteModal();
    }
  }, [org, deleteOrganization, fetchOrganizations, handleDeleteModal]);

  const filteredOrganizations = useMemo(
    () => {
      const queryFilter = allOrganizations
        .filter((organization) =>
          ['name', 'admin.name', 'advisor.firstName', 'advisor.lastName', 'status'].some((field) =>
            String(
              field
                .split('.')
                .reduce(
                  (acc, key) => (acc && acc[key as keyof typeof acc]) || '',
                  organization as any,
                ),
            )
              .toLowerCase()
              .includes(searchQuery.toLowerCase()),
          ),
        )

      const statusFilter = queryFilter.filter((organization) => filter.orgStatus !== "All" ? organization.status === filter.orgStatus : true)

      const accountTypeFilter = statusFilter.filter(({ isPayingCustomer }: ClientOrganization) => {
        if (filter.accountType === AccountType.DEMO) return !isPayingCustomer;
        if (filter.accountType === AccountType.PAYING) return !!isPayingCustomer;
        return true;
      });

      const billingIntervalFilter = accountTypeFilter.filter(({ organizationBilling }: ClientOrganization) => {
        const currentFilter = filter?.planAndBilling?.billingInterval;
        if (currentFilter?.includes(BillingIntervalSelector.ALL) || !currentFilter?.length) return true;
        const currentInterval = getOrganizationBillingData(organizationBilling)?.billingInterval as unknown as BillingIntervalSelector;

        if (!currentInterval) {
          return false;
        }

        return currentFilter?.includes(currentInterval)
      });

      const billingMethodFilter = billingIntervalFilter.filter(({ organizationBilling }: ClientOrganization) => {
        const currentFilter = filter?.planAndBilling?.billingMethod;
        if (currentFilter?.includes(BillingMethod.ALL) || !currentFilter?.length) return true;
        // The organization has no billing method but the filter is active
        const currentModel = getOrganizationBillingData(organizationBilling)?.billingModel;

        if (!currentModel) {
          return false;
        }

        if (currentFilter?.includes(BillingMethod.ALL) || !currentFilter?.length) return true
        return currentFilter?.includes(currentModel as unknown as BillingMethod)
      });

      const billingStatusFilter = billingMethodFilter.filter(({ organizationBilling }: ClientOrganization) => {
        const currentFilter = filter?.planAndBilling?.billingStatus;

        if (currentFilter?.includes(BillingStatus.ALL) || !currentFilter?.length) return true;

        const checkActiveAccounts = (currentStatus: SubscriptionStatus) => {
          const ACTIVE_STATUS = [SubscriptionStatus.Active, SubscriptionStatus.Downgrading, SubscriptionStatus.CancelingAtPeriodEnd];
          if (ACTIVE_STATUS.includes(currentStatus) && currentFilter.includes(BillingStatus.ACTIVE)) return true
          return false
        }

        // The organization has no billing status but the filter is active
        const currentStatus = getOrganizationBillingData(organizationBilling)?.status;

        if (!currentStatus) {
          return false;
        }

        return currentFilter.includes(currentStatus as unknown as BillingStatus) || checkActiveAccounts(currentStatus)
      });

      const planTypeFilter = billingStatusFilter.filter(({ currentTier }: ClientOrganization) => {
        const currentFilter = filter?.planAndBilling?.planType;
        if (currentFilter?.includes(PlanType.ALL) || !currentFilter?.length) return true;

        // The organization has no plan but the filter is active
        if (!currentTier?.code) {
          return false;
        }

        return currentFilter.includes(getBillingCategory(currentTier.code as TierCodes) as unknown as PlanType);
      });

      const teamMetricsFilter = planTypeFilter.filter(({ clientsCount }: ClientOrganization) => {
        const minEmployees = filter?.teamMetrics?.minEmployees;
        const maxEmployees = filter?.teamMetrics?.maxEmployees;
        if (minEmployees === undefined && maxEmployees === undefined) return true;
        if (!clientsCount) return false;
        if (minEmployees !== undefined && minEmployees > clientsCount) return false;
        if (maxEmployees !== undefined && maxEmployees < clientsCount) return false;
        return true;
      });

      return teamMetricsFilter;
    },
    [allOrganizations, searchQuery, filter],
  );

  // TODO: Uncomment this when we have a way to get the renewal date
  // const formatDate = (date: Date) =>
  //   date.toLocaleDateString('en-US', {
  //     month: 'short',
  //     day: '2-digit',
  //     year: 'numeric',
  //   });

  const columns = useMemo<GridColumns<ClientOrganization>>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        disableColumnMenu: true,
        flex: 1
      },
      {
        field: 'name',
        headerName: 'Company Name',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Circle
              sx={{
                width: '0.35rem',
                height: '0.35rem',
                color:
                  row.status === OrganizationStatus.Active
                    ? COLORS.POSITIVE_NEUTRAL
                    : COLORS.STATUS_NEGATIVE_NEUTRAL,
              }}
            />
            {row.name}
          </Box>
        ),
      },
      {
        field: 'organizationPlan',
        headerName: 'Plan',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => (
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: COLORS.NEUTRAL_WHITE, padding: '1rem' },
              },
            }}
            title={<PlanTooltip organization={row} />}
          >
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {row?.currentTier?.name ?? '-'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'renewalDate',
        headerName: 'Renewal Date',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => {
          const organizationBilling = getOrganizationBillingData(row?.organizationBilling);
          const nextBillingDate = organizationBilling?.nextBillingDate as string;

          if (!organizationBilling || !nextBillingDate) {
            return <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>-</Typography>;
          }

          return <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {formatDate(nextBillingDate)}
          </Typography>;
        },
      },
      {
        field: 'employees',
        headerName: 'Employees',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {row.clientsCount ?? 0}
          </Typography>
        ),
      },
      {
        field: 'seatsUsage',
        headerName: 'Seats Usage',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => {
          const organizationBilling = getOrganizationBillingData(row?.organizationBilling);

          if (!organizationBilling) {
            return <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>-</Typography>;
          }

          const usedSeats = row?.clientsCount || 0;
          const purchasedSeats = organizationBilling.purchasedSeats as number;

          return <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {usedSeats} of {purchasedSeats} used</Typography>;
        },
      },
      {
        field: 'admin',
        headerName: 'Manager',
        disableColumnMenu: true,
        flex: 1,
        valueGetter: (params) => params.row.admin.name,
      },
      {
        field: 'advisor',
        headerName: 'Advisor',
        disableColumnMenu: true,
        flex: 1,
        valueGetter: (params) => `${params.row.advisor?.firstName} ${params.row.advisor?.lastName}`,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 60,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit Organization"
            onClick={() => {
              setOrg(params.row);
              handleEditModal();
            }}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<GroupOutlinedIcon />}
            label="Manage Plan & Seats"
            onClick={() => {
              setOrg(params.row);
              handleManagePlansAndSeatsModal();
            }}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete Organization"
            onClick={() => {
              setOrg(params.row);
              handleDeleteModal();
            }}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<ToggleOnOutlinedIcon />}
            label="Manage Features"
            onClick={() => {
              setOrg(params.row);
              handleFeaturesDrawer();
            }}
            showInMenu
          />,
        ],
      },
      {
        field: 'arrowForward',
        headerName: '',
        align: 'center',
        renderCell: () => <ArrowForwardIosOutlinedIcon />,
      },
    ],
    [handleEditModal, handleDeleteModal, handleManagePlansAndSeatsModal, handleFeaturesDrawer],
  );

  const defaultCollumns = ["additionalSeats", "actions", "arrowForward"]

  return (
    <div style={{ height: 'inherit', width: '100%' }}>
      <DataGrid
        rows={filteredOrganizations.map((organization) => ({
          ...organization,
          id: organization.id,
          admin: organization.admin || { name: 'No Manager' },
          advisor: organization.advisor || { firstName: 'No', lastName: 'Advisor' },
        }))}
        columns={columns.filter(({ field }) => {
          if (defaultCollumns.includes(field as TableColumns)) return true;
          return filter.tableSettings.columns.includes(field as TableColumns);
        })}
        density={filter.tableSettings.tableDensity}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        isRowSelectable={() => false}
        loading={organizationsResponse.status === 'pending'}
        onCellClick={(params) => {
          if (params?.field === 'id') {
            toast('Organization ID was copied!', { type: 'success' });
            return navigator.clipboard.writeText(params.row.id);
          }
          if (params?.field === 'actions') return null;
          return history.push(`${paths.organizations}/${params.id}`);
        }}
        components={{
          Toolbar: () => (
            <CustomToolbar
              filter={filter}
              setFilter={setFilter}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          ),
        }}
      />
      {openManagerModal && <AssignManager handleClose={handleManagerModal} org={org as any} />}
      <OrganizationDrawer
        open={openEditModal}
        onClose={handleEditModal}
        org={org as any}
        isEdit
        advisors={advisors}
      />
      <ConfirmationModal
        icon={<DeleteOutlined />}
        title={`Are you sure you want to delete ${org?.name} from ElektraFi?`}
        onClose={handleDeleteModal}
        isOpen={openDeleteModal && org !== null}
        variant="destructive"
        confirmationText={org?.name || ''}
        onConfirm={handleDeleteOrganization}
        confirmationButtonLabel="Delete Organization"
      >
        <p>
          Deleting this organization{' '}
          <strong>
            will permanently remove access from all clients to ElektraFi and delete their financial
            data stored on the platform.
          </strong>
        </p>
        <p>To proceed with the deletion, please type the organization name: </p>
        <strong>{org?.name}</strong>
      </ConfirmationModal>
      <ManagePlansAndSeats
        open={openManagePlansAndSeatsModal}
        onClose={handleManagePlansAndSeatsModal}
        organization={org as ClientOrganization}
        fetchOrganizationBilling
      />
      <OrganizationFeaturesDrawer
        open={openFeaturesDrawer}
        onClose={handleFeaturesDrawer}
        organization={org}
      />
    </div>
  );
});
